import { FC, useState, FormEvent, useEffect } from 'react';
import { Box } from 'ui/Box';
import { Typography } from 'ui/Typography';
import { InputLabel } from 'modules/RegisterModule/components/InputLabel';
import { Button } from 'components/Button';
import { CompanyData } from 'types/api';
import { linkValidationRules } from 'utils';
export interface Step7Props {
  name?: string;
  confirm?: boolean;
  companyData: CompanyData;
  errors?: Record<string, string>;
  status?: string;
  onSubmit: (companyData: CompanyData) => void;
  prevStep?: () => void;
  setCompanyData?: (companyData: CompanyData) => void;
}
export const Step7: FC<Step7Props> = ({
  errors: registerErrors,
  name,
  companyData,
  confirm = false,
  status,
  onSubmit,
  prevStep,
  setCompanyData,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>(
    registerErrors || {}
  );

  useEffect(() => {
    if (registerErrors) {
      setErrors(registerErrors);
    }
  }, [registerErrors]);

  const onFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (confirm) {
      return onSubmit(companyData);
    }
    const formData = new FormData(event.currentTarget);
    const data = Object.fromEntries(formData.entries());
    const newErrors: Record<string, string> = {};
    for (const key in data) {
      if (key === 'website_link') {
        const link = data[key] as string;
        if (!link.match(linkValidationRules)) {
          newErrors[key] = 'Invalid URL';
        }
        if (!data[key]) {
          newErrors[key] = 'This field is required';
        }
      }
      if (!data[key]) {
        newErrors[key] = 'This field is required';
      }
    }
    if (Object.keys(newErrors).length) {
      return setErrors(newErrors);
    }
    onSubmit(data as unknown as CompanyData);
  };

  const onFormChange = (event: FormEvent) => {
    const target = event.target as HTMLInputElement;
    const name = target.name;
    const value = target.value;
    if (name === 'employees_num') {
      if (isNaN(parseInt(value))) {
        setErrors({ ...errors, employees_num: 'Invalid number' });
      } else {
        setErrors({ ...errors, employees_num: '' });
      }
    }
    if (name === 'website_link') {
      if (!value.match(linkValidationRules)) {
        setErrors({ ...errors, website_link: 'Invalid URL' });
      } else {
        setErrors({ ...errors, website_link: '' });
      }
    }
  };
  return (
    <Box className="flex h-full flex-1 flex-col items-center justify-center">
      <Typography
        variant="pSemiBold"
        className="mt-8 max-w-[44rem] text-center text-[28px] md:text-[32px]"
      >
        {confirm
          ? 'Please confirm information about the company:'
          : `It's a pleasure to meet you ${name}! Please provide information about the company:`}
      </Typography>
      <Box
        onSubmit={onFormSubmit}
        onChange={onFormChange}
        component="form"
        className="mt-8 flex w-full max-w-[40rem] items-center justify-center gap-5 md:p-4"
      >
        <Box className="flex w-full flex-col justify-center gap-8 md:gap-2">
          <InputLabel
            label="Company name"
            placeholder={confirm ? companyData.name : 'Company name'}
            name="name"
            value={companyData.name}
            error={errors.name}
            disabled={confirm}
            onChange={(name) => {
              if (name.length <= 255) {
                setCompanyData && setCompanyData({ ...companyData, name });
              }
            }}
          />
          <InputLabel
            label="Website link"
            placeholder={
              confirm ? companyData.website_link : 'https://www.company.com'
            }
            name="website_link"
            error={errors.website_link}
            value={companyData.website_link}
            disabled={confirm}
            onChange={(website_link) => {
              setCompanyData &&
                setCompanyData({ ...companyData, website_link });
            }}
          />
          <InputLabel
            label="Industry"
            placeholder={confirm ? companyData.industry : 'Industry'}
            name="industry"
            error={errors.industry}
            disabled={confirm}
            value={companyData.industry}
            onChange={(industry) => {
              if (industry.length <= 255) {
                setCompanyData && setCompanyData({ ...companyData, industry });
              }
            }}
          />
          <InputLabel
            label="Number of employees"
            placeholder={
              confirm ? companyData.employees_num : 'Number of employees'
            }
            variant="text"
            name="employees_num"
            error={errors.employees_num as unknown as string}
            disabled={confirm}
            value={companyData.employees_num}
            onChange={(employees_num) => {
              if (/^\d*$/.test(employees_num)) {
                if (
                  employees_num === '' ||
                  (employees_num !== '0' && !/^0/.test(employees_num))
                ) {
                  const numericValue = Number(employees_num);
                  if (numericValue <= 1000000) {
                    setCompanyData &&
                      setCompanyData({ ...companyData, employees_num });
                  }
                }
              }
            }}
          />
          <InputLabel
            label="Mission"
            placeholder={confirm ? companyData.mission : 'Mission'}
            variant="textarea"
            name="mission"
            error={errors.mission}
            disabled={confirm}
            value={companyData.mission}
            onChange={(mission) => {
              if (mission.length <= 255) {
                setCompanyData && setCompanyData({ ...companyData, mission });
              }
            }}
          />
          <Box className="mt-1 flex w-full flex-col justify-center gap-2 md:mt-9 md:flex-row">
            {confirm && (
              <Button
                type="button"
                variantType="outlined"
                onClick={prevStep}
                className="min-w-[120px]"
                disabled={status === 'loading'}
              >
                <Typography>Back</Typography>
              </Button>
            )}
            <Button
              type="submit"
              variantType="primary"
              className={confirm ? 'min-w-[120px]' : 'min-w-[200px]'}
              disabled={
                status === 'loading' ||
                Object.values(companyData).filter(Boolean).length <
                  Object.keys(companyData).length ||
                !!Object.values(errors).filter(Boolean).length
              }
            >
              <Typography>{confirm ? 'Confirm' : 'Next'}</Typography>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
